import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { ClinicianDTO } from '@peartherapeutics/weaver-api-client';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import { AxiosResponse } from 'axios';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { scopes } from 'utils/scope';
import AuthorizationComponent from 'components/common/AuthorizationComponent';
import {
  KakarikoGridHeaderCell,
  KakarikoGridRow,
  KakarikoEditingCell,
  KakarikoGrid,
  KakarikoSelect,
  KakarikoText,
  Validations,
  KakarikoGridHeader,
} from 'library';
import AddClinician from './AddClinician';
import { IClinician, IManageCliniciansProps } from './types';
import './styles.scss';

export const HeaderLabels = {
  firstName: 'Provider First Name',
  lastName: 'Provider Last Name',
  email: 'Email',
  status: 'Status',
  actions: 'Actions',
};

const ManageClinicians = (props: IManageCliniciansProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [clinicians, setClinicians] = useState<ClinicianDTO[]>([]);
  const { clinicName, clinicId } = props;

  const retrieveCliniciansByClinic = useCallback(
    async (locationId: string) => {
      try {
        if (locationId !== '') {
          setIsLoading(true);

          let response: AxiosResponse<ClinicianDTO[]> =
            await WeaverApiClientInstance.getAllCliniciansForLocation(
              locationId,
            );

          if (locationId === clinicId) {
            setClinicians(response.data);
          }
        } else {
          setClinicians([]);
        }
      } catch (e) {
        ErrorInterceptor.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [clinicId],
  );

  const selectStatusDisplayValue = (option: boolean) => {
    return option ? 'Active' : 'Inactive';
  };

  const updateClinicianState = (editedClinician: ClinicianDTO) => {
    const updatedClinicians = clinicians.map((clinician: ClinicianDTO) => {
      if (clinician.id === editedClinician.id) {
        return { ...editedClinician };
      }

      return clinician;
    });

    setClinicians(updatedClinicians);
  };

  const handleClinicianUpdate = async (updatedClinician: IClinician) => {
    const update = {
      id: updatedClinician.id,
      active: updatedClinician.active,
      email: updatedClinician.email,
      firstName: updatedClinician.firstName,
      lastName: updatedClinician.lastName,
      locationId: updatedClinician.locationId,
    };
    const updateClinicianResponse =
      await WeaverApiClientInstance.updateClinician(update);
    if (updateClinicianResponse.data) {
      updateClinicianState(updatedClinician);
    }
    return updateClinicianResponse.data;
  };

  const cliniciansGridHeaders = useMemo(
    () => (
      <KakarikoGridHeader>
        <KakarikoGridHeaderCell fractionWidth={3} sortName={'firstName'}>
          Provider First Name
        </KakarikoGridHeaderCell>
        <KakarikoGridHeaderCell fractionWidth={2} sortName={'lastName'}>
          Provider Last Name
        </KakarikoGridHeaderCell>
        <KakarikoGridHeaderCell fractionWidth={2} sortName={'email'}>
          Email
        </KakarikoGridHeaderCell>
        <KakarikoGridHeaderCell fractionWidth={2}>
          Status
        </KakarikoGridHeaderCell>
        <AuthorizationComponent requiredScope={scopes.UPDATE_CLINICIAN}>
          <KakarikoGridHeaderCell fractionWidth={2}>
            Actions
          </KakarikoGridHeaderCell>
        </AuthorizationComponent>
      </KakarikoGridHeader>
    ),
    [],
  );

  const rowTemplate = (clinician: ClinicianDTO) => (
    <KakarikoGridRow data={clinician} handleDataUpdate={handleClinicianUpdate}>
      <KakarikoText name={'firstName'} validation={[Validations.isNotEmpty]} />
      <KakarikoText name={'lastName'} validation={[Validations.isNotEmpty]} />
      <KakarikoText name={'email'} validation={[Validations.isNotEmpty]} />
      <KakarikoSelect
        name={'active'}
        options={[true, false]}
        validation={[Validations.isNotEmpty]}
        displayValue={selectStatusDisplayValue}
        value={clinician.active}
      />
      <AuthorizationComponent requiredScope={scopes.UPDATE_CLINICIAN}>
        <KakarikoEditingCell />
      </AuthorizationComponent>
    </KakarikoGridRow>
  );

  useEffect(() => {
    retrieveCliniciansByClinic(clinicId);
  }, [retrieveCliniciansByClinic, clinicId]);

  return (
    <div styleName="table-wrapper" data-testid="manage-clinicians">
      <AuthorizationComponent requiredScope={scopes.ADD_CLINICIAN}>
        <div
          styleName="add-clinician-wrapper"
          data-testid="add-clinician-button"
        >
          <AddClinician
            onCreateSuccess={() => retrieveCliniciansByClinic(clinicId)}
            clinicName={clinicName}
            clinicId={clinicId}
          />
        </div>
      </AuthorizationComponent>
      <KakarikoGrid
        data={clinicians}
        header={cliniciansGridHeaders}
        isLoading={isLoading}
        hasLastPageSelector={false}
        rowTemplate={rowTemplate}
        hasStickyHeader
        tableTheme={'grey'}
      />
    </div>
  );
};

export default ManageClinicians;
